import type { NavigateOptions } from 'elowen-plugin-ui-kit';
import type { PluginUiListing } from './types';
import { isPluginUserPage } from './pluginNav';

/** MOVING WITHIN THE PAGE THE READER IS ALREADY ON.
 *
 *  `/settings` and `/account` are presented as page overlays (see `app/@pageOverlay`): a CLIENT
 *  navigation to either route is answered by the overlay, whatever surface it was made from — including
 *  the deck itself. So a row that only changes `?cat=` while the reader is already standing in the deck
 *  would not move the page in front of them; it would route to the address they are already on and put a
 *  fresh mount of the same deck there, losing the section state and the scroll they were reading.
 *
 *  The seam that does move it is the document's own address. A deck page reads `window.location` and
 *  follows `popstate` (modules/account/AccountView.tsx, modules/settings/SettingsView.tsx), the record
 *  anchor reads the same pair (lib/useRowAnchor.ts), and the sidebar highlight follows the announcement
 *  (components/shell/useSidebarRoute.ts). Rewriting the address here is therefore a complete navigation
 *  for everything that cares about it, and it never reaches the router — which is what keeps interception
 *  out of a move that does not leave the page. */

/** The path part of an app href: everything before `?` and `#`. An absolute or external URL keeps its
 *  origin and so never equals a pathname, which is the right answer for one. */
export function hrefPathname(href: string): string {
  const [beforeHash = ''] = href.split('#');
  const [path = ''] = beforeHash.split('?');
  return path;
}

/** Rewrite the address to `href` and announce it, without routing.
 *
 *  `replaceState`, not push: the target is the same page by another spelling, and the overlay these decks
 *  are usually presented in already treats a section that way — Back returns to the surface the deck was
 *  opened from instead of walking back through every section visited inside it.
 *
 *  Next's own history state is carried over rather than dropped: it holds the router tree, and a later
 *  back/forward restores from it. */
export function announceLocation(href: string): void {
  window.history.replaceState(window.history.state, '', href);
  window.dispatchEvent(new PopStateEvent('popstate'));
}

/** The plugin a `/p/<plugin>…` path belongs to, from the listing the shell holds; undefined for any other
 *  path, an unknown plugin or an unloaded listing. The one place page chrome reads a plugin's manifest
 *  metadata (measure, presentation, wordmark) for the route it is drawing. */
export function pluginListingForPath(pathname: string | null, listing: PluginUiListing[] | undefined): PluginUiListing | undefined {
  const plugin = pluginOf(pathname);
  return plugin ? listing?.find((entry) => entry.name === plugin) : undefined;
}

function pluginOf(pathname: string | null): string | undefined {
  return pathname?.startsWith('/p/') ? pathname.slice(3).split('/')[0] : undefined;
}

/** The single owner of plugin overlay presentation, shared by navigation, page chrome and both hosts.
 * Exact user pages are ordinary pages even when their plugin's main bundle is an overlay. */
export function isPluginOverlayRoute(pathname: string | null, listing: PluginUiListing[] | undefined): boolean {
  const entry = pluginListingForPath(pathname, listing);
  return entry?.presentation === 'overlay' && !isPluginUserPage(entry, pathname?.split('/').slice(3).join('/') ?? '');
}

/** OPEN OR MOVE WITHIN A PLUGIN PAGE PRESENTED AS AN OVERLAY, KEEPING THE PAGE UNDER IT.
 *
 *  `/settings` and `/account` keep the page beneath their modal through an intercepting route. `/p` has
 *  none, because it would also capture every ordinary plugin page, so a router navigation to an overlay
 *  plugin swaps the page underneath for the empty `/p` route and the modal floats over nothing.
 *
 *  Next syncs `usePathname` from the native history API without routing, so this changes the address
 *  while the current route tree stays mounted and `PageOverlayHost` follows the address. The state is
 *  null on purpose: Next copies its router tree into the entry, so Back restores the page underneath.
 *  A move within the plugin already open replaces its entry, like a Settings section, so Close returns
 *  to the page underneath in one step. Explicit replacement also keeps cross-plugin callback returns
 *  out of Back history without replacing the backing route tree. */
export function openPluginOverlay(href: string, options?: NavigateOptions): void {
  const samePlugin = pluginOf(hrefPathname(href)) === pluginOf(window.location.pathname);
  if (options?.replace || samePlugin) window.history.replaceState(null, '', href);
  else window.history.pushState(null, '', href);
}

/** MOVE WITHIN THE PLUGIN PAGE THE READER IS ALREADY ON (`/p/kanban/<board>` → `/p/kanban/<board>/<card>`).
 *
 *  `/p/<plugin>/[[...rest]]` is one catch-all route whose segment key includes `rest`, so a router
 *  navigation between two sub-addresses remounts the plugin's page and scrolls the document to the top —
 *  the reader loses their place on every card opened or closed. Like the overlay move above this writes
 *  the address through the history API, which Next syncs into `usePathname` without routing; the page
 *  reads its sub-address from the pathname (`PluginHostView`) and stays mounted with its scroll.
 *
 *  A normal push (or replace on request) keeps Back closing what Forward opened. Returns false for any
 *  other move — another plugin, a core route, an external URL — which the caller routes as usual. */
export function navigateWithinPlugin(href: string, options?: NavigateOptions): boolean {
  const target = pluginOf(hrefPathname(href));
  if (!target || target !== pluginOf(window.location.pathname)) return false;
  if (options?.replace) window.history.replaceState(null, '', href);
  else window.history.pushState(null, '', href);
  return true;
}
